// src/pages/StaffPage.js
import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';

export default function StaffPage() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(null);

  const loadStaff = () => {
    api.get('/staff/users').then(r => setStaff(r.data)).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { loadStaff(); }, []);

  const toggleActive = async (id, isActive) => {
    try {
      await api.patch(`/staff/users/${id}`, { isActive: !isActive });
      toast.success(`Staff member ${!isActive ? 'activated' : 'deactivated'}.`);
      loadStaff();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed.'); }
  };

  const roleColors = { super_admin: 'badge-navy', manager: 'badge-active', finance: 'badge-sent', staff: 'badge-draft' };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Staff Users</h1>
          <p className="page-subtitle">Super Admin only</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>+ Add Staff User</button>
      </div>

      <div className="table-wrap">
        <table>
          <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Last Login</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={6} style={{ textAlign: 'center', padding: 40 }}><div className="spinner" /></td></tr>
            : staff.map(s => (
              <tr key={s.id}>
                <td className="td-primary">{s.firstName} {s.lastName}</td>
                <td style={{ fontSize: '0.875rem', color: 'var(--gray-500)' }}>{s.email}</td>
                <td><span className={`badge ${roleColors[s.role] || 'badge-draft'}`}>{s.role?.replace('_',' ')}</span></td>
                <td style={{ fontSize: '0.8rem', color: 'var(--gray-400)' }}>{s.lastLoginAt ? fmtDate(s.lastLoginAt) : 'Never'}</td>
                <td><span className={`badge badge-${s.isActive ? 'active' : 'lapsed'}`}>{s.isActive ? 'Active' : 'Inactive'}</span></td>
                <td>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => setShowResetModal(s)}>Reset PW</button>
                    <button className={`btn btn-sm ${s.isActive ? 'btn-secondary' : 'btn-primary'}`} onClick={() => toggleActive(s.id, s.isActive)}>
                      {s.isActive ? 'Deactivate' : 'Activate'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && <AddStaffModal onClose={() => setShowModal(false)} onSaved={() => { setShowModal(false); loadStaff(); }} />}
      {showResetModal && <ResetPasswordModal user={showResetModal} onClose={() => setShowResetModal(null)} />}
    </div>
  );
}

function AddStaffModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ email: '', password: '', firstName: '', lastName: '', role: 'staff' });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({...f, [k]: v}));

  const handleSave = async () => {
    if (!form.email || !form.password || !form.firstName || !form.lastName) return toast.error('All fields required.');
    if (form.password.length < 8) return toast.error('Password must be at least 8 characters.');
    setSaving(true);
    try {
      await api.post('/staff/users', form);
      toast.success('Staff user created.');
      onSaved();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed.'); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">Add Staff User</span>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">First Name</label>
              <input className="form-input" value={form.firstName} onChange={e => set('firstName', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Last Name</label>
              <input className="form-input" value={form.lastName} onChange={e => set('lastName', e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input className="form-input" type="email" value={form.email} onChange={e => set('email', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Password (min 8 chars)</label>
            <input className="form-input" type="password" value={form.password} onChange={e => set('password', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Role</label>
            <select className="form-select" value={form.role} onChange={e => set('role', e.target.value)}>
              <option value="staff">Staff</option>
              <option value="manager">Manager</option>
              <option value="finance">Finance</option>
              <option value="super_admin">Super Admin</option>
            </select>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Create User'}</button>
        </div>
      </div>
    </div>
  );
}

function ResetPasswordModal({ user, onClose }) {
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);

  const handleReset = async () => {
    if (!password || password.length < 8) return toast.error('Password must be at least 8 characters.');
    setSaving(true);
    try {
      await api.post(`/staff/users/${user.id}/reset-password`, { newPassword: password });
      toast.success(`Password reset for ${user.firstName}.`);
      onClose();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed.'); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">Reset Password — {user.firstName}</span>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">New Password (min 8 chars)</label>
            <input className="form-input" type="password" value={password} onChange={e => setPassword(e.target.value)} autoFocus />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleReset} disabled={saving}>{saving ? 'Resetting...' : 'Reset Password'}</button>
        </div>
      </div>
    </div>
  );
}

function fmtDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
